import { CloudUpload, RotateCcw } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HEALABLE_SYNC_CODES, onlineCopyText, type LocalSyncHealthDto } from '@his/shared';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { UPDATE_STATUS_KEY } from '@/lib/updates-api';
import { onlineCopyLines } from '@/lib/online-copy';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

export function OnlineCopyCard({ health, canManage }: { health: LocalSyncHealthDto; canManage: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasModule } = useAuth();
  const lines = onlineCopyLines(health, hasModule('radiology'));
  const healable = HEALABLE_SYNC_CODES.some((code) => (health.parkedByCode?.[code] ?? 0) > 0) || (health.filesGivenUp ?? 0) > 0;

  const push = useMutation({
    mutationFn: () => post('/updates/sync/push'),
    onSuccess: () => {
      toast.success(onlineCopyText('onlineCopy.sendNow'));
      void queryClient.invalidateQueries({ queryKey: UPDATE_STATUS_KEY });
    },
    onError: (error) => toast.error(onlineCopyText('onlineCopy.sendNow'), describeError(error)),
  });

  const retry = useMutation({
    mutationFn: () => post('/updates/sync/requeue-parked'),
    onSuccess: () => {
      toast.success(onlineCopyText('onlineCopy.tryAgain'));
      void queryClient.invalidateQueries({ queryKey: UPDATE_STATUS_KEY });
    },
    onError: (error) => toast.error(onlineCopyText('onlineCopy.tryAgain'), describeError(error)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <CloudUpload className="h-4 w-4 text-primary" aria-hidden /> {onlineCopyText('onlineCopy.title')}
        </CardTitle>
        <CardDescription>{lines[0]}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-1.5 text-sm">
          {lines.slice(1).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" loading={push.isPending} onClick={() => push.mutate()}>
              <CloudUpload className="h-3.5 w-3.5" /> {onlineCopyText('onlineCopy.sendNow')}
            </Button>
            {healable && (
              <Button variant="outline" size="sm" className="gap-1.5" loading={retry.isPending} onClick={() => retry.mutate()}>
                <RotateCcw className="h-3.5 w-3.5" /> {onlineCopyText('onlineCopy.tryAgain')}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
